import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, GitBranch, ArrowRight, RefreshCw, Wallet, TrendingUp } from 'lucide-react';
import { formatCurrency } from '../lib/formatters';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusPill({ status }) {
  const map = {
    'Funded': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'Paid in Full': 'bg-blue-100 text-blue-700 border-blue-200',
    'Paid in Full (Refinanced)': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'Default': 'bg-red-100 text-red-700 border-red-200',
    'Slow Pay': 'bg-amber-100 text-amber-700 border-amber-200',
  };
  const cls = map[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>{status || 'unknown'}</span>;
}

function ChainCard({ chain }) {
  const navigate = useNavigate();
  const cumulative = chain.cumulative_funding || 0;
  const lastDeal = chain.deals[chain.deals.length - 1];
  const stillActive = lastDeal && lastDeal.status === 'Funded';

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`refi-chain-${chain.deals[0].deal_id_number}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base truncate" title={chain.merchant_name}>
              {chain.merchant_name || '(unnamed merchant)'}
            </CardTitle>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
              <span className="inline-flex items-center gap-1"><GitBranch className="h-3 w-3" />{chain.chain_length} deals</span>
              <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{formatCurrency(cumulative)} cumulative</span>
              {stillActive && (
                <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><TrendingUp className="h-3 w-3" />active</span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto pb-2">
          <div className="flex items-stretch gap-2 min-w-min">
            {chain.deals.map((d, idx) => (
              <React.Fragment key={d.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/deals/${d.id}`)}
                  className="text-left bg-slate-50 hover:bg-blue-50 hover:border-blue-400 border border-slate-200 rounded-lg p-3 min-w-[10rem] transition-colors"
                  data-testid={`chain-deal-${d.deal_id_number}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono font-semibold text-sm text-blue-700">#{d.deal_id_number}</span>
                    <StatusPill status={d.status} />
                  </div>
                  <div className="text-xs text-slate-500 mb-1">{d.funding_date || '—'}</div>
                  <div className="text-sm font-bold text-slate-900">{formatCurrency(d.funding_amount)}</div>
                  {d.balance > 0 && d.status === 'Funded' && (
                    <div className="text-xs text-amber-700 mt-1">Bal {formatCurrency(d.balance)}</div>
                  )}
                </button>
                {idx < chain.deals.length - 1 && (
                  <div className="flex items-center px-1">
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function RefiChainExplorer() {
  const { getAuthHeaders } = useAuth();
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/refi-linkage/chains`, { headers: getAuthHeaders() });
      const d = await r.json();
      setChains(d.chains || []);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return chains;
    const s = search.toLowerCase().trim();
    return chains.filter((c) => {
      if ((c.merchant_name || '').toLowerCase().includes(s)) return true;
      // also match by deal id
      return c.deals.some((d) => String(d.deal_id_number || '').toLowerCase().includes(s));
    });
  }, [chains, search]);

  // Summary stats
  const stats = useMemo(() => {
    const totalChains = chains.length;
    const totalDeals = chains.reduce((sum, c) => sum + c.chain_length, 0);
    const totalDeployed = chains.reduce((sum, c) => sum + (c.cumulative_funding || 0), 0);
    const longest = chains.length ? Math.max(...chains.map(c => c.chain_length)) : 0;
    return { totalChains, totalDeals, totalDeployed, longest };
  }, [chains]);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <GitBranch className="h-6 w-6 text-indigo-600" />
              Refi Chain Explorer
            </h1>
            <p className="text-sm text-slate-600 mt-1">Visual timeline of merchant deal histories. Click any deal to open it.</p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-chains-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="pt-6">
            <div className="text-xs text-slate-500">Refi Chains</div>
            <div className="text-2xl font-bold" data-testid="stat-chains">{stats.totalChains}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="text-xs text-slate-500">Linked Deals</div>
            <div className="text-2xl font-bold" data-testid="stat-deals">{stats.totalDeals}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="text-xs text-slate-500">Cumulative Deployed</div>
            <div className="text-2xl font-bold" data-testid="stat-deployed">{formatCurrency(stats.totalDeployed)}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="text-xs text-slate-500">Longest Chain</div>
            <div className="text-2xl font-bold" data-testid="stat-longest">{stats.longest} deals</div>
          </CardContent></Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search merchant name or deal ID…"
            className="pl-9"
            data-testid="refi-chain-search"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center text-slate-500 py-12">Loading chains…</div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="text-center text-slate-500 py-12">
            {chains.length === 0 ? 'No refi chains found yet.' : 'No chains match that search.'}
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              Showing <Badge variant="outline">{filtered.length}</Badge> of {chains.length} chains
            </div>
            {filtered.map((c, i) => (
              <ChainCard key={`${c.merchant_name}-${i}`} chain={c} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
